import React from 'react'

import {palette} from 'styled-tools'
import styled from 'styled-components'
import {useDebounce, useThrottle} from 'react-use'
import {SearchResponse} from '@algolia/client-search'
import {default as searchInsights} from 'search-insights'

import {VariantList} from '@festi/common/api/rest'
import {useAlgoliaSWR} from '@festi/common/api/algolia'
import {useEvents} from '@festi/common/contexts'
import {SearchType} from '@festi/common/utils/tagmanager'

import quickSearch, {
  WordSuggestion,
  CategorySuggestions,
} from 'src/components/search/redesign/utilites/quickSearch'
import AdviceHits from './AdviceHits'
import ProductHits from './ProductHits'
import CategoryHits from './CategoryHits'
import SuggestionHits from './SuggestionHits'
import {headerMobile} from '../utilites/sizes'
import {EventIndexQueryID} from 'src/components/search/redesign/utilites/algoliaEvents'

export enum InsightType {
  product = 'productResults',
  category = 'categoriesResults',
}

export const HitsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - ${headerMobile}px);
  border-left: 1px solid ${palette('border')};
  border-right: 1px solid ${palette('border')};
  border-bottom: 1px solid ${palette('border')};
  background-color: ${palette('white')};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0px 18px 34px 0px #0417530a;
  overflow-y: auto;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 50%;
  flex: 1;
  gap: 12px;
  min-width: 300px;
  padding: 20px;
`

const Rows = styled.div`
  gap: 12px;
  height: 100%;
`

const Line = styled.div`
  width: 1px;
  background-color: ${palette('border')};
`
interface HitsProps {
  query?: string
  handleClose?: () => void
  handleSubmitQuery: (query: string, type: SearchType) => void
}

export default function QuickSearchHits({
  query,
  handleClose,
  handleSubmitQuery,
}: HitsProps) {
  const {userToken} = useEvents()

  const throttleValue = useThrottle(query, 500)

  const {data} = useAlgoliaSWR(
    throttleValue ? [throttleValue, userToken, 'search'] : undefined,
    quickSearch,
  )

  const productResults = data?.results?.[0] as SearchResponse<VariantList>
  const suggestionResults = data?.results?.[1] as SearchResponse<WordSuggestion>
  const categoriesResults = data
    ?.results?.[2] as SearchResponse<CategorySuggestions>

  const handleQuicksearchClickEvents = (
    type: InsightType,
    id: string,
    position: number,
  ) => {
    const EventType = {
      productResults: {
        index: productResults?.index,
        queryID: productResults?.queryID,
        eventName: 'Product Suggestion',
      },
      categoriesResults: {
        index: categoriesResults?.index,
        queryID: categoriesResults?.queryID,
        eventName: 'Category Suggestion',
      },
    }

    searchInsights('clickedObjectIDsAfterSearch', {
      eventName: EventType[type].eventName,
      index: EventType[type].index,
      queryID: EventType[type].queryID,
      objectIDs: [id],
      positions: [position],
    })
  }

  useDebounce(
    () => {
      if (productResults?.hits?.length) {
        searchInsights('viewedObjectIDs', {
          eventName: 'View Product Suggestion',
          index: productResults?.index,
          objectIDs: productResults?.hits?.map((hit) => hit.objectID),
        })
      }
      if (suggestionResults?.hits?.length) {
        searchInsights('viewedObjectIDs', {
          eventName: 'View Query Suggestion',
          index: suggestionResults?.index,
          objectIDs: suggestionResults?.hits?.map((hit) => hit.objectID),
        })
      }
      if (categoriesResults?.hits?.length) {
        searchInsights('viewedObjectIDs', {
          eventName: 'View Categories Suggestion',
          index: categoriesResults?.index,
          objectIDs: categoriesResults?.hits?.map((hit) => hit.objectID),
        })
      }
    },
    1000,
    [productResults],
  )

  return (
    <HitsContainer>
      <Column>
        <Rows>
          <SuggestionHits
            suggestionResults={suggestionResults}
            handleClose={handleClose}
            handleSubmitQuery={handleSubmitQuery}
          />
        </Rows>

        <Rows>
          <CategoryHits
            categoriesResults={categoriesResults}
            handleClose={handleClose}
            handleQuicksearchClickEvents={handleQuicksearchClickEvents}
          />
        </Rows>

        {false && (
          <Rows>
            <AdviceHits adviceResults={categoriesResults} />
          </Rows>
        )}
      </Column>

      <Line />

      <Column>
        <ProductHits
          productResults={productResults}
          handleClose={handleClose}
          handleQuicksearchClickEvents={handleQuicksearchClickEvents}
        />
      </Column>

      <EventIndexQueryID
        index={productResults?.index}
        queryID={productResults?.queryID}
      />
    </HitsContainer>
  )
}
